/**
 * TS — The Table Top System — unified staff identity adapter.
 * Mirrors legacy portal session tokens so KA/DA/TI hubs work unchanged.
 */

import { saveKitchenStaffToken, saveKitchenStaffPhone } from "@/lib/kitchenStaff";
import { saveDriverPartnerToken, savePartnerPhone, loadDriverPartnerToken } from "@/lib/driverPartner";
import { saveInventoryStaffToken, saveInventoryStaffPhone } from "@/lib/inventoryStaff";
import { enterStaffPortal, applyStaffPortalDocument, portalKindFromPath } from "@/lib/staffPortal";
import { isTsNeedsSignupError, loginTsFirebaseWithStatus } from "@/lib/tsEmployeeApi";
import {
  isStaffSessionAuthFailure,
  loadMirroredSessionToken,
  saveMirroredSessionToken,
  staffSessionApiError,
} from "@/lib/staffSessionStability";
import { tsAppPathsForHost } from "@/lib/teamHub";

export const TS_TOKEN_KEY = "ttt_ts_token";
export const TS_TOKEN_MIRROR_KEY = "ttt_ts_token_mirror";
export const TS_IDENTITY_KEY = "ttt_ts_identity";
export const TS_PORTION_URLS_KEY = "ttt_ts_portion_urls";
export const TS_DEPARTMENTS_KEY = "ttt_ts_departments";
export const TS_FRESH_LOGIN_KEY = "ttt_ts_fresh_login";

/**
 * Fired whenever the TS token is saved or cleared. Manage's AuthContext listens
 * for this so a same-tab save (fresh OTP login, session resume, or an API 401
 * clearing the session) is reflected immediately — the native `storage` event
 * never fires in the tab that made the write, only in other tabs.
 */
export const TS_SESSION_CHANGED_EVENT = "ttt:ts-session-changed";

/** Migrate legacy TB localStorage keys once on module load. */
function migrateLegacyTbStorage() {
  const legacyMap = [
    ["ttt_tb_token", TS_TOKEN_KEY],
    ["ttt_tb_identity", TS_IDENTITY_KEY],
    ["ttt_tb_portion_urls", TS_PORTION_URLS_KEY],
    ["ttt_tb_departments", TS_DEPARTMENTS_KEY],
  ];
  try {
    for (const [oldKey, newKey] of legacyMap) {
      const legacy = localStorage.getItem(oldKey);
      if (legacy && !localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, legacy);
      }
    }
    const portal = localStorage.getItem("ttt_staff_portal");
    if (portal === "tb") localStorage.setItem("ttt_staff_portal", "ts");
    const start = localStorage.getItem("ttt_staff_portal_tb_start");
    if (start && !localStorage.getItem("ttt_staff_portal_ts_start")) {
      localStorage.setItem("ttt_staff_portal_ts_start", start.replace(/^\/tb/, "/ts"));
    }
  } catch {
    /* private mode */
  }
}
migrateLegacyTbStorage();

function isTsAdmin(identity) {
  return Boolean(identity?.is_ts_admin || identity?.is_tb_admin);
}

function parseApiError(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.loc?.join(".") || JSON.stringify(d)).join("; ");
  }
  return fallback;
}

export function loadTsToken() {
  return loadMirroredSessionToken(TS_TOKEN_KEY, TS_TOKEN_MIRROR_KEY);
}

export function hasTsSession() {
  return Boolean(loadTsToken());
}

/** DA bootstrap — partner JWT from TS login, or TS JWT when portion token not saved yet. */
export function readDriverBootstrapToken() {
  const partner = loadDriverPartnerToken();
  if (partner) return partner;
  if (!hasTsSession()) return "";
  const identity = loadTsIdentity();
  if (!identity?.can_driver) return "";
  return loadTsToken();
}

export function saveTsToken(token) {
  saveMirroredSessionToken(TS_TOKEN_KEY, TS_TOKEN_MIRROR_KEY, token);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(TS_SESSION_CHANGED_EVENT));
  }
}

export function saveTsIdentity(identity) {
  try {
    if (identity) localStorage.setItem(TS_IDENTITY_KEY, JSON.stringify(identity));
    else localStorage.removeItem(TS_IDENTITY_KEY);
  } catch {
    /* ignore */
  }
}

export function loadTsIdentity() {
  try {
    const raw = localStorage.getItem(TS_IDENTITY_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearTsSession() {
  saveTsToken("");
  saveTsIdentity(null);
  saveTsPortionUrls(null);
  clearTsDepartments();
}

export function saveTsDepartments(departments) {
  try {
    if (departments?.length) {
      localStorage.setItem(TS_DEPARTMENTS_KEY, JSON.stringify(departments));
    } else {
      localStorage.removeItem(TS_DEPARTMENTS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function loadTsDepartments() {
  try {
    const raw = localStorage.getItem(TS_DEPARTMENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearTsDepartments() {
  saveTsDepartments(null);
}

export function allTsDepartments() {
  return ["kitchen", "driver", "inventory", "chat", "we"];
}

export function finalizeTsDepartments(identity, departments) {
  if (identity?.employee_id) {
    const available = identity?.available_departments || [];
    saveTsDepartments(available);
    return available;
  }
  if (isTsAdmin(identity)) {
    const all = allTsDepartments();
    saveTsDepartments(all);
    return all;
  }
  const available = identity?.available_departments || [];
  if (available.length <= 1) {
    saveTsDepartments(available);
    return available;
  }
  const valid = (departments || []).filter((d) => available.includes(d));
  if (valid.length < 2) return null;
  saveTsDepartments(valid);
  return valid;
}

export function completeTsOnboarding(identity, { name, departments }) {
  const saved = finalizeTsDepartments(identity, departments);
  if (!saved) throw new Error("Pick at least 2 departments");
  if (name && identity) {
    saveTsIdentity({ ...identity, name: name.trim() });
  }
  return saved;
}

export function needsTsDepartmentSetup(identity) {
  if (!identity || isTsAdmin(identity)) return false;
  if (identity.employee_id) return false;
  const available = identity.available_departments || [];
  if (available.length <= 1) return false;
  const selected = loadTsDepartments();
  if (!selected.length || selected.length < 2) return true;
  return !selected.every((d) => available.includes(d));
}

/** Set after a successful SMS OTP login (not after silent session resume). */
export function markTsFreshLogin() {
  try {
    sessionStorage.setItem(TS_FRESH_LOGIN_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** True once after OTP login, then clears the flag. */
export function consumeTsFreshLogin() {
  try {
    const v = sessionStorage.getItem(TS_FRESH_LOGIN_KEY);
    sessionStorage.removeItem(TS_FRESH_LOGIN_KEY);
    return v === "1";
  } catch {
    return false;
  }
}

/**
 * Show name/department setup only when incomplete.
 * Do not force this after every OTP — that incorrectly looked like "create your
 * profile" for staff who already finished setup (and Back then felt like logout).
 */
export function shouldShowTsOnboarding(identity, _freshLogin = false) {
  if (isDesignsOnlyIdentity(identity)) return false;
  if (!(identity?.name || "").trim()) return true;
  return needsTsDepartmentSetup(identity);
}

/** Accept ?ts=<jwt> magic links so Designs staff can open gallery without OTP. */
export function consumeTsAccessTokenFromUrl() {
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(window.location.href);
    const token = (u.searchParams.get("ts") || "").trim();
    if (!token || token.length < 40) return false;
    saveTsToken(token);
    u.searchParams.delete("ts");
    const next = `${u.pathname}${u.search}${u.hash}`;
    window.history.replaceState({}, "", next || u.pathname);
    return true;
  } catch {
    return false;
  }
}


/**
 * Silent session restore — used on every TS entry route.
 * Returns status: needs_otp | needs_onboarding | ready | rider_blocked
 */
export async function resumeTsSession() {
  consumeTsAccessTokenFromUrl();
  if (!hasTsSession()) {
    return { status: "needs_otp" };
  }

  const legacyRider = isRiderIdentity(loadTsIdentity());
  if (legacyRider) {
    clearTsSession();
    return { status: "rider_blocked" };
  }

  try {
    const data = await fetchTsMe();
    const identity = data?.identity || loadTsIdentity();
    if (isRiderIdentity(identity)) {
      clearTsSession();
      return { status: "rider_blocked" };
    }
    if (needsTsDepartmentSetup(identity)) {
      return { status: "needs_onboarding", identity, data };
    }
    return { status: "ready", identity, data };
  } catch (ex) {
    if (isRiderUseDaError(ex.message)) {
      clearTsSession();
      return { status: "rider_blocked" };
    }
    if (isStaffSessionAuthFailure(ex)) {
      clearTsSession();
      return { status: "needs_otp" };
    }
    throw ex;
  }
}

export function filterPortionsByDepartments(portions, selected, identity) {
  if (isTsAdmin(identity) || identity?.employee_id) return portions;
  if (!selected?.length) return portions;
  const set = new Set(selected);
  return portions.filter((p) => set.has(p.key));
}

export function saveTsPortionUrls(urls) {
  try {
    if (urls) localStorage.setItem(TS_PORTION_URLS_KEY, JSON.stringify(urls));
    else localStorage.removeItem(TS_PORTION_URLS_KEY);
  } catch {
    /* ignore */
  }
}

export function loadTsPortionUrls() {
  try {
    const raw = localStorage.getItem(TS_PORTION_URLS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function portionPath(key, urls) {
  const fromApi = urls?.[key];
  if (fromApi) return fromApi;
  return TS_PORTION_PATHS[key] || "/ts";
}

/** Apply portal_tokens from TS login/me onto legacy storage keys. */
export function applyTsPortalTokens(data) {
  const portal = data?.portal_tokens || {};
  const identity = data?.identity || {};
  const urls = data?.portion_urls || null;
  if (data?.token) saveTsToken(data.token);
  if (identity && Object.keys(identity).length) saveTsIdentity(identity);
  if (urls) saveTsPortionUrls(urls);
  if (identity?.phone) {
    saveKitchenStaffPhone(identity.phone);
    savePartnerPhone(identity.phone);
    saveInventoryStaffPhone(identity.phone);
  }
  if (portal.kitchen) saveKitchenStaffToken(portal.kitchen);
  if (portal.driver) saveDriverPartnerToken(portal.driver);
  if (portal.inventory) saveInventoryStaffToken(portal.inventory);
  const kitchenUrl = urls?.kitchen;
  const activePortalKind = portalKindFromPath(
    typeof window !== "undefined" ? window.location.pathname : ""
  );
  if (kitchenUrl && identity?.can_kitchen) {
    const abs = kitchenUrl.startsWith("http")
      ? kitchenUrl
      : `${window.location.origin}${kitchenUrl.startsWith("/") ? kitchenUrl : `/${kitchenUrl}`}`;
    // Do not stomp CA / DA / TI session when refreshing another TS portion.
    if (!activePortalKind || activePortalKind === "kitchen" || activePortalKind === "ts" || activePortalKind === "tb") {
      enterStaffPortal("kitchen", abs);
      applyStaffPortalDocument("kitchen");
    }
  }
}

export async function loginTsFirebase(firebaseIdToken) {
  const data = await loginTsFirebaseWithStatus(firebaseIdToken);
  if (data?.status === "ready") {
    applyTsPortalTokens(data);
  }
  return data;
}

export async function fetchTsMe() {
  const token = loadTsToken();
  if (!token) throw staffSessionApiError("Not logged in", 401);
  const params = new URLSearchParams({ token });
  const res = await fetch(`/api/ts/me?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw staffSessionApiError(parseApiError(data, "Could not load TS profile"), res.status);
  applyTsPortalTokens(data);
  return data;
}

export async function fetchTsBootstrap() {
  const token = loadTsToken();
  if (!token) throw staffSessionApiError("Not logged in", 401);
  const params = new URLSearchParams({ token });
  const res = await fetch(`/api/ts/bootstrap?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw staffSessionApiError(parseApiError(data, "Could not load TS home"), res.status);
  if (data?.identity) saveTsIdentity(data.identity);
  if (data?.portion_urls) saveTsPortionUrls(data.portion_urls);
  return data;
}

/** Portion paths inside TS (unchanged hub screens). */
export const TS_PORTION_PATHS = {
  kitchen: "/pp/hub",
  driver: "/dd/hub",
  inventory: "/ti/hub",
  chat: "/ca/hub",
  salary: "/sa/hub",
  we: "/we/hub",
};

/** Phase 2 — bundled hubs inside TS shell (legacy URLs still work). */
export const TS_APP_PORTION_PATHS = {
  kitchen: "/ts/app/kitchen",
  driver: "/ts/app/delivery",
  inventory: "/ts/app/inventory",
  chat: "/ts/app/chat",
  salary: "/ts/app/salary",
  we: "/ts/app/we",
  gallery: "/ts/app/gallery",
};

/** Portion paths for the current host (clean paths on team.thetabletop.in). */
export function tsAppPortionPaths() {
  return tsAppPathsForHost() || TS_APP_PORTION_PATHS;
}

/**
 * DG — Designs is an explicit grant.
 *
 * It used to be derived from kitchen-or-chat, which meant a Designs-only person
 * could not exist and Designs could not be withdrawn without also removing
 * Kitchen. Everyone who held it by derivation was backfilled with can_gallery,
 * so this reads the flag directly now.
 */
export function canAccessDesignGallery(identity) {
  if (!identity) return false;
  if (identity.is_ts_admin || identity.is_tb_admin) return true;
  return Boolean(identity.can_gallery);
}

/** Manage console — admin role only, decided by the server. */
export function canAccessManage(identity) {
  return Boolean(identity?.can_manage);
}

/** Designs-only staff — no Kitchen/Delivery signup or department onboarding. */
export function isDesignsOnlyIdentity(identity) {
  if (!identity?.can_gallery) return false;
  if (identity.is_ts_admin || identity.is_tb_admin) return false;
  return !(
    identity.can_kitchen ||
    identity.can_driver ||
    identity.can_inventory ||
    identity.can_chat ||
    identity.can_we ||
    identity.can_sa
  );
}

export function isRiderIdentity(identity) {
  if (!identity) return false;
  return identity.payout_kind === "rider" || Boolean(identity.is_payable_rider);
}

/** Backend 403 when a rider tries TS login — they must use DA (/dd/). */
export function isRiderUseDaError(message) {
  return String(message || "").includes("Delivery app");
}

export function tsVisiblePortions(identity, urls = loadTsPortionUrls(), options = {}) {
  const { useTsAppShell = true } = options;
  if (!identity) return [];
  const out = [];
  const appPaths = tsAppPortionPaths();
  const pathFor = (key) => {
    if (useTsAppShell && appPaths[key]) return appPaths[key];
    return portionPath(key, urls);
  };
  if (identity.can_kitchen) {
    out.push({
      key: "kitchen",
      label: "Kitchen",
      path: pathFor("kitchen"),
    });
  }
  if (identity.can_driver) {
    out.push({
      key: "driver",
      label: "Delivery",
      path: pathFor("driver"),
    });
  }
  if (identity.can_inventory) {
    out.push({
      key: "inventory",
      label: "Inventory",
      path: pathFor("inventory"),
    });
  }
  // Chat Report board lives inside TC (/ts/app/tc) — no standalone CR tile.
  if (identity.can_sa) {
    out.push({
      key: "salary",
      label: "Salary & Attendance",
      path: pathFor("salary"),
    });
  }
  if (identity.can_we) {
    out.push({
      key: "we",
      label: "Work Ethic",
      path: pathFor("we"),
    });
  }
  return out;
}

/** Redirect path when a TS identity hits a hub they must not use (null = allow). */
export function tsPortionRedirectPath(portion, pathname = "") {
  if (!hasTsSession()) return null;
  const identity = loadTsIdentity();
  if (!identity) return null;
  if (isRiderIdentity(identity)) return "/dd/hub";
  if (portion === "kitchen" && !identity.can_kitchen) {
    return identity.can_driver ? "/dd/hub" : "/ts";
  }
  if (portion === "inventory" && !identity.can_inventory) {
    return identity.can_driver ? "/dd/hub" : "/ts";
  }
  if (portion === "driver" && !identity.can_driver) return "/ts";
  if (portion === "chat") {
    // CA access is validated by /api/ca/bootstrap (Team customer_support role).
    // Stale JWT identity may lack can_chat — do not bounce to /tb on reload.
    return null;
  }
  if (portion === "salary") {
    // SA access is validated by /api/sa/bootstrap — staff roster phone match.
    return null;
  }
  if (portion === "we") {
    // WE access is validated by /api/we/bootstrap — all TS users.
    return null;
  }
  if (portion === "gallery" && !canAccessDesignGallery(identity)) {
    return "/ts";
  }
  return null;
}

export function shouldShowTsHomeNav() {
  if (!hasTsSession()) return false;
  const identity = loadTsIdentity();
  if (!identity) return false;
  return !isRiderIdentity(identity);
}

export { isTsNeedsSignupError };

export function isRiderOnly(identity) {
  if (!identity || !isRiderIdentity(identity)) return false;
  return Boolean(identity.can_driver) && !identity.can_kitchen && !identity.can_inventory;
}
